/**
 * Approvals — the queue of store applications and brand claims waiting on an admin.
 *
 * Two record types share one worklist:
 *   - Store applications (`/admin/store-approvals`, storeApprovalService): a
 *     store organization in `pending_verification`, or the legacy
 *     `pending_approval`. Approving it activates the store.
 *   - Brand claims (`/vendor-approval`, vendorApprovalService). Approving a claim
 *     is the act that verifies an organization against a brand, so its case
 *     panel spells out every consequence before the button is pressed.
 *
 * Until MOT-91 this page only read brand claims, so a store the Organizations
 * directory showed as "pending" never appeared here.
 *
 * Pending holds everything still open; Decided holds recent approved and
 * rejected claims. The store queue has no history endpoint, so a decided store
 * application leaves the queue rather than moving to Decided.
 *
 * Carry requests (MOT-159, flag `distributor_carry`) are the third segment: a
 * distributor or wholesaler asking to carry brands it does not own. Approving
 * one lands the brand ids on the vendor's `catalogBrandIds` (its catalog
 * browser and warehouse matching), never on the brand's claim, and grants no
 * MAP or rebate right; it can be revoked here later.
 *
 * On an approved request the admin can also grant MAP authority per carried
 * brand (MOT-406, flag `distributor_map_authority` for the vendor): the grant
 * lets the distributor publish MAP for that brand only, never rebates or
 * dealer cost. Above the carry queue, "Not carried" (MOT-407) lists warehouse
 * brands Promaster matching skipped because they are not on the approved list,
 * with an action that approves carry for the brand.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, ButtonBase, CircularProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ValidatedTextField } from '../../components/ui/ValidatedTextField';
import { formatFirestoreDateOnly } from '../../utils/firestoreDate';
import {
  brandDirectoryApi,
  BrandRepInviteEvidence,
  ClaimEvidence,
  VendorApprovalRequest,
} from '../../services/brandDirectoryApi';
import {
  ADMIN_CARRY_REQUESTS_QUERY_KEY,
  ADMIN_NOT_CARRIED_QUERY_KEY,
  approveCarryRequest,
  approveNotCarried,
  CarryRequest,
  DistributorCarrySkip,
  listAdminCarryRequests,
  listNotCarried,
  rejectCarryRequest,
  revokeCarryRequest,
  setCarryMapAuthority,
} from '../../services/carryRequestsApi';
import {
  formatStoreAddress,
  StoreApplication,
  storeApprovalApi,
  storeCaseId,
} from '../../services/storeApprovalApi';
import {
  bvCardSx,
  bvColor,
  bvContentSx,
  bvPageSx,
  bvRadius,
  bvTransition,
  bvType,
} from './brandVendorsTokens';
import {
  BvButton,
  BvCard,
  DefinitionRows,
  BvEmptyState,
  Eyebrow,
  BvPageHeader,
  BvSwitch,
  SegmentedFilter,
  StatusDot,
} from './components/BrandVendorsPrimitives';

const REJECT_REASONS = ['Ownership not proven', 'Wrong brand', 'Duplicate org'];

/** How many decided claims of each outcome the Decided tab shows. */
const DECIDED_LIMIT = 25;

type QueueTab = 'pending' | 'decided' | 'carry';

type QueueItem =
  | { kind: 'claim'; id: string; request: VendorApprovalRequest }
  | { kind: 'store'; id: string; application: StoreApplication }
  | { kind: 'carry'; id: string; carry: CarryRequest };

const CARRY_EVIDENCE_LABEL: Record<CarryRequest['evidence']['kind'], string> = {
  line_card: 'Line card',
  invoice: 'Distributor invoice',
  warehouse_feed: 'Warehouse feed',
};

const CARRY_STATUS_LABEL: Record<CarryRequest['status'], string> = {
  pending: 'Carry request',
  approved: 'Carry approved',
  rejected: 'Carry rejected',
  revoked: 'Carry revoked',
};

/** Pending first (oldest first, so the longest wait is on top), then decided newest first. */
const carryOrder = (a: CarryRequest, b: CarryRequest) => {
  const aOpen = a.status === 'pending' ? 0 : 1;
  const bOpen = b.status === 'pending' ? 0 : 1;
  if (aOpen !== bOpen) return aOpen - bOpen;
  return aOpen === 0 ? toTime(a.createdAt) - toTime(b.createdAt) : toTime(b.updatedAt) - toTime(a.updatedAt);
};

const timeAgo = (value?: string | Date | null) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hours = Math.round((Date.now() - date.getTime()) / 36e5);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'Yesterday' : `${days} days ago`;
};

const toTime = (value?: string | Date | null) => {
  if (!value) return 0;
  const time = (value instanceof Date ? value : new Date(value)).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/** Relative age plus the absolute date, so "3 days ago" can be pinned to a calendar day. */
const ageWithDate = (value?: string | Date | null) => {
  const age = timeAgo(value);
  if (!age) return '';
  return `${age} · ${formatFirestoreDateOnly(value)}`;
};

type EvidenceOutcome = 'ok' | 'warn' | 'bad';

const OUTCOME_INK: Record<EvidenceOutcome, string> = {
  ok: bvColor.success,
  warn: bvColor.warning,
  bad: bvColor.danger,
};

interface EvidenceRow {
  label: string;
  note: string;
  state: string;
  outcome: EvidenceOutcome;
  /** Signed link to the submitted artefact, where the API supplies one. */
  href?: string;
  /** Re-checked against live data (DNS, competing owner), so meaningless once the case is decided. */
  live?: boolean;
}

/**
 * Flatten the four proof signals into display rows.
 * A competing claim is the only signal that can block outright, so it maps to
 * "Blocking" rather than a soft warning.
 */
const evidenceRows = (evidence?: ClaimEvidence, adminInvite?: BrandRepInviteEvidence): EvidenceRow[] => {
  const email = evidence?.emailDomain;
  const letter = evidence?.dealerLetter;
  const dns = evidence?.dnsTxt;
  const competing = evidence?.competingClaim;

  // Only a claim that came from a Motzu invitation sent to the rep's own verified address has this row.
  const invited: EvidenceRow[] = adminInvite
    ? [
        {
          label: 'Invited by Motzu',
          note: `Invitation to ${adminInvite.email} sent ${formatFirestoreDateOnly(adminInvite.invitedAt)}; they signed up with that verified address`,
          state: 'Invited',
          outcome: 'ok',
        },
      ]
    : [];

  return [
    ...invited,
    {
      label: 'Work email domain matches brand website',
      note:
        email?.email && email?.brandHost
          ? `${email.email} vs ${email.brandHost}`
          : 'No domain comparison on file',
      state: email?.status === 'verified' ? 'Verified' : email?.status === 'mismatch' ? 'Mismatch' : 'Missing',
      outcome: email?.status === 'verified' ? 'ok' : email?.status === 'mismatch' ? 'bad' : 'warn',
    },
    {
      label: 'Dealer / authorisation letter',
      note: letter?.fileName || 'No letter uploaded',
      state: letter?.status === 'uploaded' ? 'Uploaded' : 'Missing',
      outcome: letter?.status === 'uploaded' ? 'ok' : 'warn',
      // The admin has to be able to read the letter before approving on it.
      href: letter?.url,
    },
    {
      label: 'DNS TXT record on the brand domain',
      note: dns?.token ? `${dns.host || 'brand domain'} · ${dns.token}` : 'No verification token placed',
      state:
        dns?.status === 'verified'
          ? 'Verified'
          : dns?.status === 'pending'
            ? 'Pending'
            : dns?.status === 'failed'
              ? 'Failed'
              : 'Missing',
      outcome: dns?.status === 'verified' ? 'ok' : dns?.status === 'failed' ? 'bad' : 'warn',
      live: true,
    },
    {
      label: competing?.status === 'clear' || !competing ? 'No competing claim' : 'Competing claim',
      note: competing?.ownerOrgName
        ? `Already owned by ${competing.ownerOrgName}`
        : 'No competing owner — the brand is unclaimed',
      state: competing?.status === 'clear' || !competing ? 'Clear' : 'Blocking',
      outcome: competing?.status === 'clear' || !competing ? 'ok' : 'bad',
      live: true,
    },
  ];
};

export const BrandVendorsApprovalsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(params.get('case'));
  const [tab, setTab] = useState<QueueTab>(
    params.get('tab') === 'decided' ? 'decided' : params.get('tab') === 'carry' ? 'carry' : 'pending'
  );
  const [rejectOpen, setRejectOpen] = useState(false);

  const storeQuery = useQuery({
    queryKey: ['store-approvals', 'pending'],
    queryFn: () => storeApprovalApi.listPending(),
  });
  const pendingQuery = useQuery({
    queryKey: ['vendor-approvals', 'pending'],
    queryFn: () => brandDirectoryApi.listApprovals('pending'),
  });
  const needsInfoQuery = useQuery({
    queryKey: ['vendor-approvals', 'needs_info'],
    queryFn: () => brandDirectoryApi.listApprovals('needs_info'),
  });
  const rejectedQuery = useQuery({
    queryKey: ['vendor-approvals', 'rejected'],
    queryFn: () => brandDirectoryApi.listApprovals('rejected'),
  });
  const approvedQuery = useQuery({
    queryKey: ['vendor-approvals', 'approved'],
    queryFn: () => brandDirectoryApi.listApprovals('approved'),
  });
  const carryQuery = useQuery({
    queryKey: ADMIN_CARRY_REQUESTS_QUERY_KEY,
    queryFn: () => listAdminCarryRequests(),
  });
  const notCarriedQuery = useQuery({
    queryKey: ADMIN_NOT_CARRIED_QUERY_KEY,
    queryFn: () => listNotCarried(),
    enabled: tab === 'carry',
  });

  const isLoading =
    storeQuery.isLoading ||
    pendingQuery.isLoading ||
    needsInfoQuery.isLoading ||
    rejectedQuery.isLoading ||
    approvedQuery.isLoading ||
    carryQuery.isLoading;
  const error =
    storeQuery.error ||
    pendingQuery.error ||
    needsInfoQuery.error ||
    rejectedQuery.error ||
    approvedQuery.error ||
    carryQuery.error;

  // Open cases only: store applications first (oldest first, as the API sorts
  // them), then brand claims waiting on an admin, then claims waiting on the
  // requester.
  const pendingItems = useMemo<QueueItem[]>(
    () => [
      ...(storeQuery.data || []).map(
        (application): QueueItem => ({ kind: 'store', id: storeCaseId(application.organizationId), application })
      ),
      ...[...(pendingQuery.data || []), ...(needsInfoQuery.data || [])].map(
        (request): QueueItem => ({ kind: 'claim', id: request.id, request })
      ),
    ],
    [storeQuery.data, pendingQuery.data, needsInfoQuery.data]
  );

  // History: most recently decided first.
  const decidedItems = useMemo<QueueItem[]>(
    () =>
      [
        ...(rejectedQuery.data || []).slice(0, DECIDED_LIMIT),
        ...(approvedQuery.data || []).slice(0, DECIDED_LIMIT),
      ]
        .sort((a, b) => toTime(b.reviewedAt || b.submittedAt) - toTime(a.reviewedAt || a.submittedAt))
        .map((request): QueueItem => ({ kind: 'claim', id: request.id, request })),
    [rejectedQuery.data, approvedQuery.data]
  );

  const carryItems = useMemo<QueueItem[]>(
    () => [...(carryQuery.data || [])].sort(carryOrder).map((carry): QueueItem => ({ kind: 'carry', id: carry.id, carry })),
    [carryQuery.data]
  );
  const pendingCarryCount = (carryQuery.data || []).filter((carry) => carry.status === 'pending').length;

  const items = tab === 'pending' ? pendingItems : tab === 'decided' ? decidedItems : carryItems;

  // A deep link to a decided case (e.g. from a brand page) opens the Decided tab.
  useEffect(() => {
    if (!selectedId || isLoading) return;
    if (tab === 'pending' && !pendingItems.some((item) => item.id === selectedId)) {
      if (decidedItems.some((item) => item.id === selectedId)) setTab('decided');
      else if (carryItems.some((item) => item.id === selectedId)) setTab('carry');
    }
    // Only when the data first settles or the selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, isLoading]);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || items[0] || null,
    [items, selectedId]
  );

  // Keep the URL in step so a chip or task deep-link survives a refresh.
  useEffect(() => {
    if (isLoading) return;
    const wantCase = selected?.id || null;
    const wantTab = tab === 'pending' ? null : tab;
    if (params.get('case') !== wantCase || params.get('tab') !== wantTab) {
      const next: Record<string, string> = {};
      if (wantCase) next.case = wantCase;
      if (wantTab) next.tab = wantTab;
      setParams(next, { replace: true });
    }
  }, [selected, tab, isLoading, params, setParams]);

  useEffect(() => {
    setRejectOpen(false);
  }, [selected?.id]);

  const switchTab = (next: QueueTab) => {
    setTab(next);
    setSelectedId(null);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vendor-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['store-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['brand-directory'] });
    queryClient.invalidateQueries({ queryKey: ['organization-directory'] });
    queryClient.invalidateQueries({ queryKey: ['admin-organizations'] });
    queryClient.invalidateQueries({ queryKey: ['vendor-approval-pending'] });
    queryClient.invalidateQueries({ queryKey: ADMIN_CARRY_REQUESTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ADMIN_NOT_CARRIED_QUERY_KEY });
  };

  const selectedClaim = selected?.kind === 'claim' ? selected.request : null;
  const selectedStore = selected?.kind === 'store' ? selected.application : null;
  const selectedCarry = selected?.kind === 'carry' ? selected.carry : null;

  // `forceTransfer` is required when the brand already has a competing claim;
  // the service returns 409 without it rather than silently reassigning.
  const approveMutation = useMutation({
    mutationFn: (forceTransfer?: boolean) =>
      brandDirectoryApi.approveRequest(selectedClaim!.id, undefined, forceTransfer),
    onSuccess: invalidate,
  });
  const rejectMutation = useMutation({
    mutationFn: (reason: string) => brandDirectoryApi.rejectRequest(selectedClaim!.id, reason),
    onSuccess: () => {
      setRejectOpen(false);
      invalidate();
    },
  });
  // Only a needs-info request can go back to the queue. There is no backend path
  // that reopens an approved or rejected case, so decided cases are terminal here.
  const returnToQueueMutation = useMutation({
    mutationFn: () => brandDirectoryApi.returnToPending(selectedClaim!.id),
    onSuccess: invalidate,
  });
  const requestInfoMutation = useMutation({
    mutationFn: (notes: string) => brandDirectoryApi.requestInfo(selectedClaim!.id, notes),
    onSuccess: invalidate,
  });

  const approveStoreMutation = useMutation({
    mutationFn: (notes?: string) => storeApprovalApi.approve(selectedStore!.organizationId, notes),
    onSuccess: () => {
      setSelectedId(null);
      invalidate();
    },
  });
  const rejectStoreMutation = useMutation({
    mutationFn: (reason: string) => storeApprovalApi.reject(selectedStore!.organizationId, reason),
    onSuccess: () => {
      setSelectedId(null);
      invalidate();
    },
  });
  const requestStoreInfoMutation = useMutation({
    mutationFn: (message: string) => storeApprovalApi.requestInfo(selectedStore!.organizationId, message),
    onSuccess: invalidate,
  });

  const approveCarryMutation = useMutation({
    mutationFn: (note?: string) => approveCarryRequest(selectedCarry!.id, note),
    onSuccess: invalidate,
  });
  const rejectCarryMutation = useMutation({
    mutationFn: (reason: string) => rejectCarryRequest(selectedCarry!.id, reason),
    onSuccess: invalidate,
  });
  const revokeCarryMutation = useMutation({
    mutationFn: (reason: string) => revokeCarryRequest(selectedCarry!.id, reason),
    onSuccess: invalidate,
  });
  const mapAuthorityMutation = useMutation({
    mutationFn: ({ brandId, granted }: { brandId: string; granted: boolean }) =>
      setCarryMapAuthority(selectedCarry!.id, brandId, granted),
    onSuccess: invalidate,
  });
  const approveNotCarriedMutation = useMutation({
    mutationFn: (id: string) => approveNotCarried(id),
    onSuccess: invalidate,
  });

  const mutations = [
    approveMutation,
    rejectMutation,
    returnToQueueMutation,
    requestInfoMutation,
    approveStoreMutation,
    rejectStoreMutation,
    requestStoreInfoMutation,
    approveCarryMutation,
    rejectCarryMutation,
    revokeCarryMutation,
    mapAuthorityMutation,
    approveNotCarriedMutation,
  ];
  const busy = mutations.some((mutation) => mutation.isPending);
  const mutationError = mutations.find((mutation) => mutation.error)?.error;

  return (
    <Box sx={bvPageSx}>
      <Box sx={{ ...bvContentSx, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <BvPageHeader
          title="Approvals"
          description="Store applications and brand claims in one queue — approving a claim is what verifies the org against that brand."
        />

        <Box>
          <SegmentedFilter<QueueTab>
            ariaLabel="Approvals queue"
            value={tab}
            onChange={switchTab}
            options={[
              { value: 'pending', label: 'Pending', count: pendingItems.length },
              { value: 'decided', label: 'Decided', count: decidedItems.length },
              { value: 'carry', label: 'Carry requests', count: pendingCarryCount },
            ]}
          />
        </Box>

        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {mutationError && <Alert severity="error">{(mutationError as Error).message}</Alert>}

        {tab === 'carry' && (notCarriedQuery.data?.length || notCarriedQuery.error) ? (
          <NotCarriedCard
            skips={notCarriedQuery.data || []}
            error={notCarriedQuery.error as Error | null}
            busy={busy}
            onApprove={(id) => approveNotCarriedMutation.mutate(id)}
          />
        ) : null}

        {isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: '80px' }}>
            <CircularProgress size={22} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={bvCardSx}>
            {tab === 'pending' ? (
              <BvEmptyState
                headline="Nothing waiting for review"
                note="Store applications and brand claims land here until an admin decides them."
              />
            ) : tab === 'carry' ? (
              <BvEmptyState
                headline="No carry requests"
                note="A distributor or wholesaler asking to carry brands it does not own lands here. Approving opens those brands in its catalog browser; it never changes who owns the brand."
              />
            ) : (
              <BvEmptyState
                headline="No decided cases yet"
                note="Approved and rejected brand claims are listed here."
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '340px 1fr' },
              gap: '16px',
              alignItems: 'start',
            }}
          >
            {/* queue */}
            <BvCard>
              {items.map((item) => (
                <QueueRow
                  key={item.id}
                  item={item}
                  selected={item.id === selected?.id}
                  onSelect={() => setSelectedId(item.id)}
                />
              ))}
            </BvCard>

            {/* case panel */}
            {selectedClaim && (
              <CasePanel
                request={selectedClaim}
                busy={busy}
                rejectOpen={rejectOpen}
                onOpenReject={() => setRejectOpen(true)}
                onCancelReject={() => setRejectOpen(false)}
                onApprove={(forceTransfer) => approveMutation.mutate(forceTransfer)}
                onReject={(reason) => rejectMutation.mutate(reason)}
                onReturnToQueue={() => returnToQueueMutation.mutate()}
                onRequestInfo={() => requestInfoMutation.mutate('More proof of representation needed.')}
              />
            )}
            {selectedStore && (
              <StoreCasePanel
                key={selectedStore.organizationId}
                application={selectedStore}
                busy={busy}
                onApprove={(notes) => approveStoreMutation.mutate(notes)}
                onReject={(reason) => rejectStoreMutation.mutate(reason)}
                onRequestInfo={(message) => requestStoreInfoMutation.mutate(message)}
              />
            )}
            {selectedCarry && (
              <CarryCasePanel
                key={selectedCarry.id}
                carry={selectedCarry}
                busy={busy}
                onApprove={(note) => approveCarryMutation.mutate(note)}
                onReject={(reason) => rejectCarryMutation.mutate(reason)}
                onRevoke={(reason) => revokeCarryMutation.mutate(reason)}
                onSetMapAuthority={(brandId, granted) => mapAuthorityMutation.mutate({ brandId, granted })}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const QueueRow: React.FC<{ item: QueueItem; selected: boolean; onSelect: () => void }> = ({
  item,
  selected,
  onSelect,
}) => {
  const isStore = item.kind === 'store';
  const isCarry = item.kind === 'carry';
  const request = item.kind === 'claim' ? item.request : null;
  const application = item.kind === 'store' ? item.application : null;
  const carry = item.kind === 'carry' ? item.carry : null;

  const dotColor = carry
    ? carry.status === 'approved'
      ? bvColor.success
      : carry.status === 'rejected'
        ? bvColor.danger
        : carry.status === 'revoked'
          ? bvColor.muted
          : bvColor.warning
    : request?.status === 'approved'
      ? bvColor.success
      : request?.status === 'rejected'
        ? bvColor.danger
        : bvColor.warning;
  const title = isCarry
    ? `${carry!.vendorName} wants to carry ${carry!.brandNames.join(', ')}`
    : isStore
      ? `${application!.name} wants to join as a store`
      : `${request!.vendorName} wants to claim ${request!.brandName}`;
  const subtitle = isCarry
    ? `${CARRY_EVIDENCE_LABEL[carry!.evidence.kind]}${carry!.evidence.proof ? ' · file attached' : carry!.evidence.link ? ' · link' : ' · no document'}`
    : isStore
      ? application!.primaryContactEmail || application!.businessName || '—'
      : request!.contactInfo?.primaryContact?.email || request!.companyInfo?.legalName || '—';
  const chip = isCarry
    ? CARRY_STATUS_LABEL[carry!.status]
    : isStore
      ? 'Store application'
      : request!.status === 'approved'
        ? 'Approved'
        : request!.status === 'rejected'
          ? 'Rejected'
          : request!.status === 'needs_info'
            ? 'Brand claim · info requested'
            : 'Brand claim';
  const when = isCarry
    ? ageWithDate(carry!.status === 'pending' ? carry!.createdAt : carry!.updatedAt)
    : isStore
      ? ageWithDate(application!.submittedAt)
      : ageWithDate(request!.status === 'approved' || request!.status === 'rejected' ? request!.reviewedAt || request!.submittedAt : request!.submittedAt);

  return (
    <ButtonBase
      aria-pressed={selected}
      onClick={onSelect}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        px: '13px',
        py: '11px',
        borderBottom: `1px solid ${bvColor.borderSubtle}`,
        bgcolor: selected ? bvColor.surfaceTint : 'transparent',
        transition: bvTransition,
        '&:hover': { bgcolor: selected ? bvColor.surfaceTint : bvColor.page },
        '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: -2 },
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
        <StatusDot color={dotColor} size={7} sx={{ mt: '7px' }} />
        <Box
          sx={{
            ...bvType.tablePrimary,
            color: bvColor.ink,
            // Wrap rather than truncate: the brand is the end of the title.
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
          title={title}
        >
          {title}
        </Box>
      </Box>
      <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '4px', ml: '14px' }}>{subtitle}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '7px', ml: '14px' }}>
        <Box
          component="span"
          sx={{
            px: '7px',
            py: '1px',
            borderRadius: `${bvRadius.chip}px`,
            border: `1px solid ${bvColor.border}`,
            color: bvColor.ink3,
            ...bvType.microCaption,
          }}
        >
          {chip}
        </Box>
        <Box sx={{ ...bvType.microCaption, color: bvColor.muted }}>{when}</Box>
      </Box>
    </ButtonBase>
  );
};

/**
 * A store application: what the store submitted at signup, and the three
 * decisions storeApprovalService supports. Reject and request-info both need
 * text (the API refuses them without it), so each opens its own inline form.
 */
const StoreCasePanel: React.FC<{
  application: StoreApplication;
  busy: boolean;
  onApprove: (notes?: string) => void;
  onReject: (reason: string) => void;
  onRequestInfo: (message: string) => void;
}> = ({ application, busy, onApprove, onReject, onRequestInfo }) => {
  const [mode, setMode] = useState<'idle' | 'reject' | 'info'>('idle');
  const [text, setText] = useState('');

  const open = (next: 'reject' | 'info') => {
    setMode(next);
    setText('');
  };
  const cancel = () => {
    setMode('idle');
    setText('');
  };

  const address = formatStoreAddress(application.address);
  const legacyStatus = application.status === 'pending_approval';

  return (
    <BvCard>
      <Box sx={{ px: '16px', py: '14px', borderBottom: `1px solid ${bvColor.borderSubtle}` }}>
        <Box sx={{ ...bvType.peekTitle, color: bvColor.ink }}>{application.name} wants to join as a store</Box>
        <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '4px' }}>
          {[
            application.primaryContactName && `Requested by ${application.primaryContactName}`,
            application.primaryContactEmail,
            application.submittedAt && `submitted ${timeAgo(application.submittedAt)}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Box>
      </Box>

      {application.infoRequest && (
        <Box sx={{ px: '16px', pt: '14px' }}>
          <Alert severity="info">
            Information requested {timeAgo(application.infoRequest.requestedAt)}: {application.infoRequest.message}
          </Alert>
        </Box>
      )}

      {/* submitted at signup */}
      <Box sx={{ px: '16px', pt: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>Submitted at signup</Eyebrow>
        <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
          <DefinitionRows
            rows={[
              { label: 'Business name', value: application.businessName || application.name },
              { label: 'Website', value: application.website || 'Not provided' },
              { label: 'Address', value: address || 'Not provided' },
              { label: 'Tax ID', value: application.taxId || 'Not provided' },
              {
                label: 'Brands sold',
                value: application.brandsSold.length ? application.brandsSold.join(', ') : 'None listed',
              },
              {
                label: 'Status',
                value: legacyStatus
                  ? 'pending_approval (older status — decides the same way)'
                  : application.status,
              },
            ]}
          />
        </Box>
      </Box>

      {/* effects */}
      <Box sx={{ px: '16px', pt: '16px', pb: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>What approving does</Eyebrow>
        <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
          <DefinitionRows
            rows={[
              { label: 'Store status', value: 'Moves to active' },
              { label: 'Unlocks', value: 'Integrations, pricing and sync, immediately' },
              { label: 'Notifies', value: 'The store owner, by email' },
            ]}
          />
        </Box>
      </Box>

      {/* footer */}
      <Box sx={{ bgcolor: bvColor.page, borderTop: `1px solid ${bvColor.border}`, px: '16px', py: '13px' }}>
        {mode === 'idle' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <BvButton size="md" tone="primary" onClick={() => onApprove()} disabled={busy}>
              {busy ? 'Working…' : `Approve ${application.name}`}
            </BvButton>
            <BvButton size="md" tone="danger" onClick={() => open('reject')} disabled={busy}>
              Reject…
            </BvButton>
            <BvButton size="md" onClick={() => open('info')} disabled={busy}>
              Request info…
            </BvButton>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.caption, color: bvColor.muted, textAlign: 'right', maxWidth: 280 }}>
              Approving activates the store immediately and emails the owner.
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: mode === 'reject' ? bvColor.dangerBg : bvColor.surface,
              border: `1px solid ${mode === 'reject' ? bvColor.dangerBorder : bvColor.border}`,
              borderRadius: '10px',
              p: '13px',
            }}
          >
            <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink, mb: '8px' }}>
              {mode === 'reject'
                ? 'Reason — kept internal; the store is told it was not approved'
                : 'Message — the store sees this in the app'}
            </Box>
            <ValidatedTextField
              validate={(value) => (value.trim() ? null : 'Required')}
              fullWidth
              multiline
              minRows={2}
              size="small"
              autoFocus
              value={text}
              onChange={(event) => setText(event.target.value)}
              inputProps={{ maxLength: 1000, 'aria-label': mode === 'reject' ? 'Rejection reason' : 'Message to the store' }}
            />
            <Box sx={{ display: 'flex', gap: '8px', mt: '10px', justifyContent: 'flex-end' }}>
              <BvButton size="sm" onClick={cancel} disabled={busy}>
                Cancel
              </BvButton>
              <BvButton
                size="sm"
                tone={mode === 'reject' ? 'danger' : 'primary'}
                disabled={busy || !text.trim()}
                onClick={() => {
                  if (mode === 'reject') onReject(text.trim());
                  else onRequestInfo(text.trim());
                  setMode('idle');
                }}
              >
                {mode === 'reject' ? 'Reject application' : 'Send request'}
              </BvButton>
            </Box>
          </Box>
        )}
      </Box>
    </BvCard>
  );
};

const CasePanel: React.FC<{
  request: VendorApprovalRequest;
  busy: boolean;
  rejectOpen: boolean;
  onOpenReject: () => void;
  onCancelReject: () => void;
  onApprove: (forceTransfer?: boolean) => void;
  onReject: (reason: string) => void;
  onReturnToQueue: () => void;
  onRequestInfo: () => void;
}> = ({
  request,
  busy,
  rejectOpen,
  onOpenReject,
  onCancelReject,
  onApprove,
  onReject,
  onReturnToQueue,
  onRequestInfo,
}) => {
  const decided = request.status === 'approved' || request.status === 'rejected';
  // Live checks (DNS TXT, competing owner) are not frozen at decision time, so
  // once a case is decided they would only show a stale "Pending". Hide them.
  const rows = evidenceRows(request.evidence, request.adminInvite).filter((row) => !(decided && row.live));
  const contact = request.contactInfo?.primaryContact;
  const blocking = !decided && rows.some((row) => row.outcome === 'bad');

  // Mirrors the service's state machine: approve accepts only `pending`, reject
  // accepts `pending` or `needs_info`, and only `needs_info` can return to the
  // queue. Approved and rejected cases have no reopen path, so they are terminal.
  const needsInfo = request.status === 'needs_info';
  const canApprove = request.status === 'pending';
  // A competing claim makes approval a deliberate ownership transfer.
  const competing = request.evidence?.competingClaim.status;
  const needsTransfer = competing === 'claimed' || competing === 'pending_other';

  return (
    <BvCard>
      <Box sx={{ px: '16px', py: '14px', borderBottom: `1px solid ${bvColor.borderSubtle}` }}>
        <Box sx={{ ...bvType.peekTitle, color: bvColor.ink }}>
          {request.vendorName} wants to claim {request.brandName}
        </Box>
        <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '4px' }}>
          {[
            contact?.name && `Requested by ${contact.name}`,
            contact?.email,
            request.submittedAt && `submitted ${ageWithDate(request.submittedAt)}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Box>
      </Box>

      {/* evidence */}
      <Box sx={{ px: '16px', pt: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>Evidence</Eyebrow>
        <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
          {rows.map((row, index) => (
            <Box
              key={row.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                px: '13px',
                py: '11px',
                borderTop: index === 0 ? 'none' : `1px solid ${bvColor.borderSubtle}`,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 18,
                  height: 18,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${OUTCOME_INK[row.outcome]}`,
                  color: OUTCOME_INK[row.outcome],
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {row.outcome === 'ok' ? '✓' : row.outcome === 'warn' ? '!' : '×'}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink }}>{row.label}</Box>
                {row.href ? (
                  <Box
                    component="a"
                    href={row.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      ...bvType.microCaption,
                      color: bvColor.accent,
                      mt: '2px',
                      '&:hover': { color: bvColor.accentHover },
                    }}
                  >
                    {row.note} ↗
                  </Box>
                ) : (
                  <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '2px' }}>{row.note}</Box>
                )}
              </Box>
              <Box sx={{ ...bvType.caption, fontWeight: 500, color: OUTCOME_INK[row.outcome], whiteSpace: 'nowrap' }}>
                {row.state}
              </Box>
            </Box>
          ))}
          {decided && (
            <Box
              sx={{
                px: '13px',
                py: '11px',
                borderTop: `1px solid ${bvColor.borderSubtle}`,
                ...bvType.caption,
                color: bvColor.muted,
              }}
            >
              Checks stopped when decided — DNS and competing-claim checks are no longer re-run.
            </Box>
          )}
        </Box>
      </Box>

      {/* effects */}
      <Box sx={{ px: '16px', pt: '16px', pb: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>What approving does</Eyebrow>
        <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
          <DefinitionRows
            rows={[
              { label: 'Brand ownership', value: `Org: ${request.vendorName} → Brand: ${request.brandName}` },
              { label: 'Org verification', value: 'Org becomes verified for this brand only' },
              { label: 'Vendor rights', value: 'MAP publishing and product-data edits on this brand’s catalog' },
              { label: 'Lifecycle stage', value: 'Ingesting → Claimed' },
            ]}
          />
        </Box>
      </Box>

      {/* footer */}
      <Box sx={{ bgcolor: bvColor.page, borderTop: `1px solid ${bvColor.border}`, px: '16px', py: '13px' }}>
        {decided ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <StatusDot color={request.status === 'approved' ? bvColor.success : bvColor.danger} size={7} />
            <Box sx={{ ...bvType.tableSecondary, color: bvColor.ink }}>
              {request.status === 'approved' ? 'Approved' : 'Rejected'}
              {request.rejectionReason ? ` — ${request.rejectionReason}` : ''}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
              {request.reviewedAt ? `Decided ${ageWithDate(request.reviewedAt)}` : 'Decided'}
            </Box>
          </Box>
        ) : rejectOpen ? (
          <Box
            sx={{
              bgcolor: bvColor.dangerBg,
              border: `1px solid ${bvColor.dangerBorder}`,
              borderRadius: '10px',
              p: '13px',
            }}
          >
            <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink }}>
              Reason — the requester sees this
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '10px' }}>
              {REJECT_REASONS.map((reason) => (
                <BvButton key={reason} size="sm" tone="danger" disabled={busy} onClick={() => onReject(reason)}>
                  {reason}
                </BvButton>
              ))}
              <Box sx={{ flex: 1 }} />
              <BvButton size="sm" onClick={onCancelReject} disabled={busy}>
                Cancel
              </BvButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            {canApprove &&
              (needsTransfer ? (
                // The brand already has an owner, so approving is an explicit
                // transfer rather than a plain approval.
                <BvButton size="md" tone="primary" onClick={() => onApprove(true)} disabled={busy}>
                  {busy ? 'Working…' : `Transfer ${request.brandName} to ${request.vendorName}`}
                </BvButton>
              ) : (
                <BvButton size="md" tone="primary" onClick={() => onApprove()} disabled={busy}>
                  {busy ? 'Working…' : `Approve claim on ${request.brandName}`}
                </BvButton>
              ))}
            {needsInfo && (
              <BvButton size="md" tone="primary" onClick={onReturnToQueue} disabled={busy}>
                {busy ? 'Working…' : 'Return to queue'}
              </BvButton>
            )}
            <BvButton size="md" tone="danger" onClick={onOpenReject} disabled={busy}>
              Reject…
            </BvButton>
            {canApprove && (
              <BvButton size="md" onClick={onRequestInfo} disabled={busy}>
                Request info
              </BvButton>
            )}
            <Box sx={{ flex: 1 }} />
            <Box
              sx={{
                ...bvType.caption,
                color: needsTransfer || blocking ? bvColor.danger : bvColor.muted,
                textAlign: 'right',
                maxWidth: 280,
              }}
            >
              {needsInfo
                ? 'Waiting on the requester. Return it to the queue once they respond.'
                : needsTransfer
                  ? `${request.brandName} already has an owner — approving transfers it away from them.`
                  : blocking
                    ? 'A blocking signal is unresolved — check the evidence before approving.'
                    : 'Approving transfers ownership immediately and notifies the requester.'}
            </Box>
          </Box>
        )}
      </Box>
    </BvCard>
  );
};

/**
 * A carry request: the brands, the evidence the distributor sent and the three
 * decisions. Reject and revoke need a reason the vendor reads, so each opens
 * its own inline form. Approving is not a claim: it spells out what it does
 * and does not grant before the button is pressed.
 */
const CarryCasePanel: React.FC<{
  carry: CarryRequest;
  busy: boolean;
  onApprove: (note?: string) => void;
  onReject: (reason: string) => void;
  onRevoke: (reason: string) => void;
  onSetMapAuthority: (brandId: string, granted: boolean) => void;
}> = ({ carry, busy, onApprove, onReject, onRevoke, onSetMapAuthority }) => {
  const [mode, setMode] = useState<'idle' | 'reject' | 'revoke'>('idle');
  const [text, setText] = useState('');

  const open = (next: 'reject' | 'revoke') => {
    setMode(next);
    setText('');
  };
  const cancel = () => {
    setMode('idle');
    setText('');
  };

  const pending = carry.status === 'pending';
  const approved = carry.status === 'approved';
  const proof = carry.evidence.proof;

  return (
    <BvCard>
      <Box sx={{ px: '16px', py: '14px', borderBottom: `1px solid ${bvColor.borderSubtle}` }}>
        <Box sx={{ ...bvType.peekTitle, color: bvColor.ink }}>
          {carry.vendorName} wants to carry {carry.brandNames.length} brand{carry.brandNames.length === 1 ? '' : 's'}
        </Box>
        <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '4px' }}>
          {[
            carry.source === 'backfill' ? 'Recorded by Motzu from the warehouse feed' : 'Requested by the vendor',
            carry.createdAt && `sent ${ageWithDate(carry.createdAt)}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Box>
      </Box>

      {/* brands and evidence */}
      <Box sx={{ px: '16px', pt: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>Brands requested</Eyebrow>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {carry.brandNames.map((name, index) => (
            <Box
              key={carry.brandIds[index] || name}
              component="span"
              sx={{
                px: '8px',
                py: '2px',
                borderRadius: `${bvRadius.chip}px`,
                border: `1px solid ${bvColor.border}`,
                color: bvColor.ink,
                ...bvType.caption,
              }}
            >
              {name}
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ px: '16px', pt: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>Evidence</Eyebrow>
        <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
          <DefinitionRows
            rows={[
              { label: 'Kind', value: CARRY_EVIDENCE_LABEL[carry.evidence.kind] },
              {
                label: 'Document',
                value: proof ? (
                  <Box
                    component="a"
                    href={proof.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: bvColor.accent, '&:hover': { color: bvColor.accentHover } }}
                  >
                    {proof.fileName} ↗
                  </Box>
                ) : (
                  'No file uploaded'
                ),
              },
              {
                label: 'Link',
                value: carry.evidence.link ? (
                  <Box
                    component="a"
                    href={carry.evidence.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: bvColor.accent, overflowWrap: 'anywhere', '&:hover': { color: bvColor.accentHover } }}
                  >
                    {carry.evidence.link} ↗
                  </Box>
                ) : (
                  'None'
                ),
              },
              { label: 'Note from the vendor', value: carry.evidence.note || 'None' },
            ]}
          />
        </Box>
      </Box>

      {/* effects */}
      <Box sx={{ px: '16px', pt: '16px', pb: '14px' }}>
        <Eyebrow sx={{ mb: '7px' }}>What approving does</Eyebrow>
        <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
          <DefinitionRows
            rows={[
              { label: 'Catalog scope', value: 'The brands open in the vendor’s product browser and warehouse matching' },
              { label: 'Brand ownership', value: 'Unchanged — carry is not a claim and is not exclusive' },
              {
                label: 'Vendor rights',
                value: 'No rebates, dealer cost or product edits on these brands; MAP only where granted below',
              },
              { label: 'Later', value: 'Revoke here to take the brands back' },
            ]}
          />
        </Box>
      </Box>

      {approved && (
        <Box sx={{ px: '16px', pb: '14px' }}>
          <Eyebrow sx={{ mb: '7px' }}>MAP authority</Eyebrow>
          <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
            {carry.brandIds.map((brandId, index) => {
              const grant = carry.mapAuthority?.[brandId];
              const granted = grant?.granted === true;
              const name = carry.brandNames[index] || brandId;
              return (
                <Box
                  key={brandId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    px: '12px',
                    py: '9px',
                    borderTop: index === 0 ? 'none' : `1px solid ${bvColor.borderSubtle}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ ...bvType.tableSecondary, color: bvColor.ink }}>{name}</Box>
                    <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
                      {granted
                        ? `Manages MAP · granted ${ageWithDate(grant?.grantedAt)}`
                        : grant?.revokedAt
                          ? `MAP stays with the brand owner · revoked ${ageWithDate(grant.revokedAt)}`
                          : 'MAP stays with the brand owner'}
                    </Box>
                  </Box>
                  <BvSwitch
                    checked={granted}
                    disabled={busy}
                    label={`${granted ? 'Revoke' : 'Grant'} MAP authority for ${name}`}
                    onChange={(next) => onSetMapAuthority(brandId, next)}
                  />
                </Box>
              );
            })}
          </Box>
          <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '6px' }}>
            {carry.mapAuthorityEnabled
              ? 'Granting lets the distributor publish MAP for that brand from its MAP page. Rebates and dealer cost stay with the brand owner.'
              : 'Distributor MAP authority is off for this vendor, so a grant is recorded but takes effect only once the flag is on.'}
          </Box>
        </Box>
      )}

      {/* footer */}
      <Box sx={{ bgcolor: bvColor.page, borderTop: `1px solid ${bvColor.border}`, px: '16px', py: '13px' }}>
        {mode !== 'idle' ? (
          <Box
            sx={{
              bgcolor: bvColor.dangerBg,
              border: `1px solid ${bvColor.dangerBorder}`,
              borderRadius: '10px',
              p: '13px',
            }}
          >
            <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink, mb: '8px' }}>
              Reason — the vendor sees this
            </Box>
            <ValidatedTextField
              validate={(value) => (value.trim() ? null : 'Required')}
              fullWidth
              multiline
              minRows={2}
              size="small"
              autoFocus
              value={text}
              onChange={(event) => setText(event.target.value)}
              inputProps={{ maxLength: 1000, 'aria-label': mode === 'reject' ? 'Rejection reason' : 'Revocation reason' }}
            />
            <Box sx={{ display: 'flex', gap: '8px', mt: '10px', justifyContent: 'flex-end' }}>
              <BvButton size="sm" onClick={cancel} disabled={busy}>
                Cancel
              </BvButton>
              <BvButton
                size="sm"
                tone="danger"
                disabled={busy || !text.trim()}
                onClick={() => {
                  if (mode === 'reject') onReject(text.trim());
                  else onRevoke(text.trim());
                  setMode('idle');
                }}
              >
                {mode === 'reject' ? 'Reject request' : 'Revoke carry'}
              </BvButton>
            </Box>
          </Box>
        ) : pending ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <BvButton size="md" tone="primary" onClick={() => onApprove()} disabled={busy}>
              {busy ? 'Working…' : `Approve carry for ${carry.vendorName}`}
            </BvButton>
            <BvButton size="md" tone="danger" onClick={() => open('reject')} disabled={busy}>
              Reject…
            </BvButton>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.caption, color: bvColor.muted, textAlign: 'right', maxWidth: 280 }}>
              Approving opens the brands in the vendor’s catalog immediately. It does not change who owns them.
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <StatusDot
              color={approved ? bvColor.success : carry.status === 'rejected' ? bvColor.danger : bvColor.muted}
              size={7}
            />
            <Box sx={{ ...bvType.tableSecondary, color: bvColor.ink }}>
              {CARRY_STATUS_LABEL[carry.status].replace('Carry ', '').replace(/^./, (c) => c.toUpperCase())}
              {carry.decisionNote ? ` — ${carry.decisionNote}` : ''}
            </Box>
            {approved && (
              <BvButton size="sm" tone="danger" onClick={() => open('revoke')} disabled={busy}>
                Revoke…
              </BvButton>
            )}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
              {carry.revokedAt
                ? `Revoked ${ageWithDate(carry.revokedAt)}`
                : carry.decidedAt
                  ? `Decided ${ageWithDate(carry.decidedAt)}`
                  : 'Decided'}
            </Box>
          </Box>
        )}
      </Box>
    </BvCard>
  );
};

/**
 * Warehouse brands a distributor's matching skipped because they are not on
 * its approved carry list (MOT-407). One row per brand with the rows skipped
 * in the last pass and a sample, and an action that approves carry for it.
 */
const NotCarriedCard: React.FC<{
  skips: DistributorCarrySkip[];
  error: Error | null;
  busy: boolean;
  onApprove: (id: string) => void;
}> = ({ skips, error, busy, onApprove }) => (
  <BvCard
    title={`Not carried · ${skips.length} brand${skips.length === 1 ? '' : 's'}`}
    caption="Warehouse rows matching skipped because the brand is not on the distributor's approved carry list. Nothing is matched for these brands until carry is approved."
  >
    {error && (
      <Box sx={{ px: '16px', py: '10px' }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    )}
    <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
      {skips.map((skip) => (
        <Box
          key={skip.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            px: '16px',
            py: '10px',
            minHeight: 48,
            borderTop: `1px solid ${bvColor.borderSubtle}`,
          }}
        >
          <StatusDot color={bvColor.warning} size={7} />
          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
            <Box sx={{ ...bvType.tableSecondary, color: bvColor.ink, fontWeight: 500 }}>
              {skip.brand} · {skip.lastRunRows} row{skip.lastRunRows === 1 ? '' : 's'} skipped
            </Box>
            <Box sx={{ ...bvType.caption, color: bvColor.muted, overflowWrap: 'anywhere' }}>
              {[
                skip.sampleRows.map((row) => row.sku || row.name).filter(Boolean).join(', '),
                `seen in ${skip.runCount} pass${skip.runCount === 1 ? '' : 'es'}`,
                skip.lastSeenAt && `last ${timeAgo(skip.lastSeenAt).toLowerCase()}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Box>
          </Box>
          <BvButton size="sm" tone="primary" onClick={() => onApprove(skip.id)} disabled={busy}>
            Approve carry for {skip.brand}
          </BvButton>
        </Box>
      ))}
    </Box>
  </BvCard>
);

export default BrandVendorsApprovalsPage;
